import { useQuery } from '@apollo/client/react';
import { differenceInMonths } from 'date-fns';
import type { Point } from 'geojson';
import { camelCase, isEmpty, kebabCase, startCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CityWithJobCount, Match } from '@pflegia/data-access';
import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { MapLazyLoad, useMultipleVariantData } from '@pflegia/feature-flags';
import { TransportationType } from '@pflegia/types/api/candidate/transportation-type';
import { CareType } from '@pflegia/types/api/employer/care-type';
import { EmployerStatus } from '@pflegia/types/api/employer/employer-status';
import { JobPosition, JobPositionForCandidateDto } from '@pflegia/types/api/job-position/job-position';
import { JobPositionStatus } from '@pflegia/types/api/job-position/job-position-status';
import { JobTitle } from '@pflegia/types/api/job-title/job-title-type';
import { ConnectionType } from '@pflegia/types/api/match/connection-type';
import { ConnectionStatus } from '@pflegia/types/common/enums/connection-status.enum';
import { ScrollableSectionEnum } from '@pflegia/types/common/enums/scrollable-section-enum';
import { SuggestionsTrigger } from '@pflegia/types/common/enums/suggestions-trigger.enum';
import { TrackingSource } from '@pflegia/types/common/enums/tracking-source';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { useJobListSortStore } from '@pflegia/ui/hooks/use-sort';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import { getButtonClassName } from '@pflegia/ui/shared/button/Button.component';
import LoadingModal from '@pflegia/ui/shared/dialog/LoadingModal';
import useCandidateLocationForJob from '@pflegia/ui/shared/hooks/use-candidate-location-for-job';
import ScrollableSection, { ScrollableSectionProps } from '@pflegia/ui/shared/scrollable-section';
import OverviewNavigationSkeleton from '@pflegia/ui/shared/sticky-overview-navigation/OverviewNavigationSkeleton';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';
import { canHaveGeoArea } from '@pflegia/util/functions/employer';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import { HighlightsList, PerksList } from '@hooks/get-job-position.hook';
import { useAnonymized } from '@hooks/use-anonymize';
import { useRouterHelper } from '@hooks/use-router-helper';

import BothCheckingPhoneSmiling from '@public/assets/both-checking-phone-smiling.png';
import initiativePersonImage from '@public/assets/male-with-thumb-up.png';
import Map from '@public/assets/map.png';

import { JOBS, MONTHS_BEFORE_SALARY_EXPIRY } from '@shared/application/constants';
import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useSuggestionModalLogic } from '@shared/components/dialogue/hooks/use-suggestion-modal-logic';
import TrustpilotWidget, { TrustpilotWidgetType } from '@shared/components/trustpilot-widget/TrustpilotWidget';
import { ROUTE_CHANGE_ERROR } from '@shared/constants/route-change-error';
import { JobDetailsProvider, useJobDetails } from '@shared/context/job-details.context';
import { useTrackJobPositionFeatures } from '@shared/hooks/use-tracking-job-position-features';
import { AB_TEST_SECTION_KEYS, getJdpSectionsForVariant, JdpSectionKey } from '@shared/utils/get-jdp-sections-for-variant';

import { scrollTopAuto } from '@utils/scroll-top-auto';

import { useJobPositionsFeatures } from '../job-board/hooks/use-job-positions-features';
import { ContainerSectionSkeleton, JobOverviewSkeleton } from './JobOverviewSkeleton';
import { ComponentWithApplyButtonInterface } from './application/interfaces';
import OtherJobsCarousel, { CarouselJob } from './components/ab-test-new-components/OtherJobsCarousel';
import SectionViewedTracker from './components/ab-test-new-components/SectionViewedTracker';
import useJdpAbTestVariant from './components/ab-test-new-components/use-jdp-ab-test-variant';
import BackLink from './components/back-link/BackLink';
import JobDetailTitle from './components/job-overview/JobDetailTitle';
import MatchReasons from './components/match-reasons/MatchReasons';
import { hasValidCoordinates } from './components/match-reasons/format-distance';
import NavigationCluster from './components/navigation-cluster/NavigationCluster';
import StickyBottomSection from './components/sticky-bottom-section/StickyBottomSection.component';
import StickySection from './components/sticky-section/StickySection';
import WhyThisJobHeader from './components/why-this-job/WhyThisJobHeader';
import WhyThisJobSection from './components/why-this-job/WhyThisJobSection';
import { usePausedJobModal } from './hooks/use-paused-job-modal';
import { getSortedLocationsByDistance } from './utils/get-sorted-locations-by-distance';

const Career = dynamic(() => import('@shared/components/career/Career'));

const WrappedSuggestionsModal = dynamic(() => import('@shared/components/dialogue/suggestions-modal/WrappedModal'), { ssr: false });

const JobOverview = dynamic(() => import('./components/job-overview/JobOverview.component'), {
  loading: () => <JobOverviewSkeleton />,
});
const MagazinSliderSection = dynamic(() => import('../magazin/components/magazin-slider-section/MagazinSliderSection'), {
  ssr: false,
});
const JobPositionFaqSection = dynamic(() => import('./components/job-position-faq-section/JobPositionFaqSection'), {
  ssr: false,
});

const ContainerSection = dynamic(() => import('@pflegia/ui/shared/container-section'), {
  loading: () => <ContainerSectionSkeleton />,
});

const InvitationText = dynamic(() => import('./components/invitation-text/InvitationText.component'));

const DirectionsMapWrapper = dynamic(() => import('@pflegia/ui/shared/map/DirectionsMapWrapper'), {
  ssr: false,
});

const MapWithZonesWrapper = dynamic(() => import('./components/map-with-zones/MapWithZonesWrapper'), {
  ssr: false,
});

const EmployerDifferentiation = dynamic(() => import('./components/employer-differentiations/EmployerDifferentiations.component'));
const SummaryCard = dynamic(() => import('./components/job-overview/SummaryCard'));
const ContactPersonTransparent = dynamic(() =>
  import('./components/job-overview/ContactPersonTransparent').then(mod => mod.ContactPersonTransparent),
);
const Insights = dynamic(() => import('./components/insights/Insights.component'), { ssr: false });

// Sub-sections previously rendered inside JobPositionSection which was dynamic({ ssr: false }).
// Keeping ssr: false preserves that client-only behaviour and avoids hydration mismatches in dangerouslySetInnerHTML-based sub-sections like OnBoardingProcedure.
const OpeningHours = dynamic(() => import('./components/opening-hours/OpeningHours.component'), { ssr: false });
const ShiftSystem = dynamic(() => import('./components/shift-system/ShiftSystem.component'), { ssr: false });
const YourProfile = dynamic(() => import('./components/your-profile/YourProfile.component'), { ssr: false });
const Responsibilities = dynamic(() => import('./components/responsibilities/Responsibilities.component'), { ssr: false });
const WhatWeOffer = dynamic(() => import('./components/what-we-offer/WhatWeOffer.component'), { ssr: false });
const DocumentationSystems = dynamic(() => import('./components/documentation-systems/DocumentationSystems.component'), { ssr: false });
// eslint-disable-next-line eslint-compat/naming-convention
const HowIWork = dynamic(() => import('./components/how-i-work/HowIWork.component'), { ssr: false });
const OnBoardingProcedure = dynamic(() => import('./components/onboarding-procedure/OnBoardingProcedure.component'), { ssr: false });
const Arguments = dynamic(() => import('./components/arguments/Arguments.component'), { ssr: false });
const Gallery = dynamic(() => import('@pflegia/ui/shared/gallery/Gallery'), { ssr: false });
const EmployeeTestimony = dynamic(() => import('./components/employee-testimony/EmployeeTestimony.component'), { ssr: false });
// eslint-disable-next-line eslint-compat/naming-convention
const WhatADayLooksLike = dynamic(() => import('./components/what-a-day-looks-like/WhatADayLooksLike.component'), { ssr: false });
const InitiativeBanner = dynamic(() => import('./components/initiative-banner/InitiativeBanner'), { ssr: false });

const Modal = dynamic(() => import('@pflegia/ui/shared/modal/Modal'), { ssr: false });

const OurTeam = dynamic(() => import('./components/our-team/OurTeam.component'));
const RecommendationComponent = dynamic(() => import('./components/recommendation/Recommendation.component'));
const SalaryDetails = dynamic(() => import('@pflegia/ui/shared/salary-details/SalaryDetails.component'));

const AboutUs = dynamic(() => import('./components/about-us/AboutUs.component'));
const ClientAssignment = dynamic(() => import('./components/client-assignment/ClientAssignment.component'));
const Highlights = dynamic(() => import('./components/highlights/Highlights.component'));
const ApplicationHeader = dynamic(() => import('./components/job-overview/ApplicationHeader'));

const StickyOverviewNavigation = dynamic(() => import('@pflegia/ui/shared/sticky-overview-navigation/StickyOverviewNavigation'), {
  ssr: false,
  loading: () => <OverviewNavigationSkeleton />,
});
const JobPausedWithSuggestions = dynamic(() => import('./components/job-paused/JobPausedWithSuggestions.component'), { ssr: false });
// JobPositionSection dissolved - sub-sections hoisted to this file for data-driven reordering
const OffersDialogueModal = dynamic(() => import('@components/dialogue/OffersDialogueModal'), { loading: () => <LoadingModal /> });
const FeaturesBadges = dynamic(() => import('@pflegia/ui/shared/job-position/FeaturesBadges').then(mod => mod.FeaturesBadges), {
  ssr: false,
});
const OtherOpenPositions = dynamic(() => import('./components/other-open-positions/OtherOpenPositions.component'), { ssr: false });

const INTERSECTION_ROOT_MARGIN = '-300px 0px 0px 0px';
export interface JobDetailProps {
  match?: Match;
  matchLoading?: boolean;
  matchReasonsIsTest?: boolean;
  jobPosition: JobPosition;
  perks: PerksList;
  highlights: HighlightsList;
  validThrough?: string;
  relatedJobPositions?: JobPositionForCandidateDto[];
  alternativeJobTitlesByPriority?: JobTitle[];
  nearbyCities?: CityWithJobCount[];
}

const JobDetailBase: React.FC<JobDetailProps> = ({
  match,
  matchLoading,
  matchReasonsIsTest,
  jobPosition,
  perks,
  highlights,
  relatedJobPositions,
  alternativeJobTitlesByPriority,
  nearbyCities,
}) => {
  const { isTransparentPath, isPreview, isExternalJob, isTransparentJobDetails, isAnyJobPage, isSlimmed, isJobDetailsPage } =
    useRouterHelper();
  const authenticated = useAuth();
  const router = useRouter();
  const { t } = useTranslation('jobDetails');
  const { isPraxia } = useFlavorFromRoute();
  const { invitationText, connectionType } = match || {};
  const invitationType = (connectionType ?? 'default') as CardType;
  const status = (match?.connectionStatus ?? 'pending') as CardStatus;
  const { data: candidateData } = useQuery(CandidateDocument, { skip: !authenticated });
  const candidate = candidateData?.candidate;
  const candidateLocationsWithCoordinates = (candidate?.locations ?? []).filter(location =>
    hasValidCoordinates(location.location?.coordinates),
  );
  const { candidateLocation } = useCandidateLocationForJob(candidateLocationsWithCoordinates, jobPosition.geoLocation);
  const jobOfferDetails = useJobDetails();
  const {
    setMatchDetails,
    relatedJobs: { loading, jobs },
  } = jobOfferDetails ?? {};

  const isJobPositionPaused = jobPosition.status === JobPositionStatus.Paused;
  const { isTest: isTestJdp, isFlagReady } = useJdpAbTestVariant(jobPosition.isInitiative || false, isJobPositionPaused);
  const { sections, visibleSections, orderedSections } = getJdpSectionsForVariant(jobPosition, isTestJdp);

  const salaryUpdatedAt = jobPosition.salaryLastChangedAt ?? jobPosition.createdAt;
  const diffInMonths = differenceInMonths(new Date(), salaryUpdatedAt);
  const showSalary = !jobPosition.isInitiative || diffInMonths <= MONTHS_BEFORE_SALARY_EXPIRY;
  const navigationOptions = sections
    .filter(section => {
      if (section === ScrollableSectionEnum.Salary) return showSalary;
      if (section === ScrollableSectionEnum.Onboarding) return isJobDetailsPage;
      return true;
    })
    .map(section => ({
      key: section,
      label: t(`navigationOptions.${camelCase(section)}`),
      anchorLink: section,
    }));
  const anchorClassName = isTransparentPath ? 'top-[-75px] lg:top-[-220px]' : 'top-[-75px] lg:top-[-130px]';
  const isAnonymous = useAnonymized();

  const [suggestionModalAllowed, setSuggestionModalAllowed] = useState<boolean>();

  const isPendingInvite = useMemo(
    () => connectionType === ConnectionType.Invitation && status === ConnectionStatus.Pending,
    [connectionType, status],
  );

  const connectionRef = useRef({ connectionType, status });
  useEffect(() => {
    if (suggestionModalAllowed === undefined && isPendingInvite) {
      setSuggestionModalAllowed(isPendingInvite);
    }
  }, [suggestionModalAllowed, isPendingInvite]);

  useEffect(() => {
    if (
      suggestionModalAllowed === undefined &&
      connectionType === ConnectionType.Application &&
      status === ConnectionStatus.Pending &&
      connectionRef.current.connectionType === undefined &&
      connectionRef.current.status === ConnectionStatus.Pending
    ) {
      setSuggestionModalAllowed(true);
    }
  }, [connectionType, isPendingInvite, status, suggestionModalAllowed]);

  const [enableSuggestionModalOnExit, setEnableSuggestionModalOnExit] = useState(isPendingInvite);
  const { handleOpen: handleOpenSuggestionsModal, prepareSuggestionsStoreData } = useSuggestionModalLogic();

  const sectionsOnScreenRef = useRef(
    Object.fromEntries(navigationOptions.map(section => [section, false])) as Record<ScrollableSectionEnum, boolean>,
  );
  const { open: offersDialogueShown, onOpen, setCallback } = useMatchDialogueStore();

  const [pendingRoute, setPendingRoute] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    scrollTopAuto(0);
  }, []);

  // Job Position Features Handling
  const { featureByJobPositionId, loading: featuresLoading, isCompleted } = useJobPositionsFeatures([jobPosition.id]);
  const featuresBadges = useMemo(() => featureByJobPositionId[jobPosition.id] ?? {}, [featureByJobPositionId, jobPosition.id]);
  const features = useMemo(() => ({ ...featuresBadges, loading: featuresLoading }), [featuresBadges, featuresLoading]);
  const { handleTracking } = useTrackJobPositionFeatures();
  const { sort: sortedBy } = useJobListSortStore.getState();

  const source = useMemo(() => {
    if (isTransparentJobDetails) return TrackingSource.TransparentJobDetails;
    if (isAnyJobPage) return TrackingSource.JobDetails;
  }, [isTransparentJobDetails, isAnyJobPage]);

  useEffect(() => {
    if (isCompleted) {
      handleTracking(TrackingEventName.JobPositionViewed, {
        // @ts-expect-error Generic types clash
        jobPosition,
        featuresBadges: features,
        source,
        slimmedJobCard: isSlimmed,
        authenticated,
        // eslint-disable-next-line eslint-compat/camelcase
        sorted_by: startCase(sortedBy),
      });
    }
  }, [features, featuresBadges, handleTracking, isCompleted, jobPosition, router.asPath, source, isSlimmed, authenticated, sortedBy]);

  useEffect(() => {
    const onRouteChangeStart = (route: string) => {
      const phoneNumberModal = document.getElementById('phone-number-modal');
      if (phoneNumberModal) return;
      const currentPath = router.asPath;
      const shouldShowModal =
        !confirmed &&
        !offersDialogueShown &&
        currentPath !== route &&
        invitationType === ConnectionType.Invitation &&
        status === ConnectionStatus.Pending;
      if (shouldShowModal) {
        setEnableSuggestionModalOnExit(false);
        const jobPositionId = jobPosition.id;
        const details: ConnectionInfo = {
          status: 'feedback',
          type: 'default',
          jobPositionId,
          jobPositionTitle: jobPosition.title,
        };
        setMatchDetails(details);
        onOpen(details);
        setPendingRoute(route);
        prepareSuggestionsStoreData({ trigger: SuggestionsTrigger.IgnoreInvite, initialNavigation: route, jobPositionId });
        setCallback(
          'onClose',
          () => {
            setConfirmed(true);
            setEnableSuggestionModalOnExit(true);
          },
          'before',
        );
        router.events.emit('routeChangeError');
        throw ROUTE_CHANGE_ERROR;
      }
    };

    router.events.on('routeChangeStart', onRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
    };
  }, [
    router,
    offersDialogueShown,
    invitationType,
    confirmed,
    jobPosition.id,
    jobPosition.title,
    pendingRoute,
    status,
    setMatchDetails,
    onOpen,
    setCallback,
    handleOpenSuggestionsModal,
    prepareSuggestionsStoreData,
  ]);

  useEffect(() => {
    if (confirmed && pendingRoute && !enableSuggestionModalOnExit) {
      prepareSuggestionsStoreData({ initialNavigation: pendingRoute, jobPositionId: jobPosition.id });
      router.push(pendingRoute);
      setConfirmed(false);
    }
  }, [confirmed, enableSuggestionModalOnExit, jobPosition.id, pendingRoute, prepareSuggestionsStoreData, router]);

  const handleSectionIntersection = React.useCallback<NonNullable<ScrollableSectionProps['onIntersectionChange']>>((section, entry) => {
    if (sectionsOnScreenRef?.current)
      sectionsOnScreenRef.current = {
        ...sectionsOnScreenRef.current,
        [section]: entry.isIntersecting,
      };
  }, []);

  const titleRef = useRef<HTMLDivElement>(null);

  const openDialog = useCallback<ComponentWithApplyButtonInterface['openDialog']>(
    matchInfo => {
      setMatchDetails(matchInfo);
      onOpen(matchInfo);
    },
    [onOpen, setMatchDetails],
  );

  const sortedLocations = useMemo(() => {
    const locations = [jobPosition.employer.geoLocation, ...(jobPosition.employer.patientAreas || [])].filter(
      (location): location is NonNullable<typeof location> => location !== null,
    );
    return candidateLocation ? getSortedLocationsByDistance(locations, candidateLocation) : locations;
  }, [candidateLocation, jobPosition.employer.geoLocation, jobPosition.employer.patientAreas]);
  const { isTest } = useMultipleVariantData(MapLazyLoad.Name);

  const { showPausedModal, setShowPausedModal } = useJobDetails();
  const { elementRef: salaryRef } = usePausedJobModal({
    enabled:
      (jobPosition.status === JobPositionStatus.Paused || jobPosition.employer.status === EmployerStatus.LocationPaused) &&
      !isPreview &&
      !isExternalJob &&
      isJobDetailsPage &&
      !jobPosition.isInitiative,
  });

  const relatedPositions = relatedJobPositions ?? jobs;
  const relatedPositionsLoading = relatedJobPositions ? false : loading;

  const coverImage =
    (jobPosition.employer.gallery && jobPosition.employer.gallery.length > 0 && getPublicImageUrl(jobPosition.employer.gallery[0])) || '';
  const isCoverImageShown = Boolean(jobPosition.isInitiative && coverImage);
  const hideWhyThisJobSection = isTestJdp === null;

  const Details = (
    <ContainerSection>
      {matchReasonsIsTest && !match?.connectionType && (
        <MatchReasons jobPosition={jobPosition} matchLoading={matchLoading} candidateLocation={candidateLocation} showSalary={showSalary} />
      )}
      {isTransparentJobDetails && (
        <BackLink
          /* @ts-expect-error Generated vs custom type */
          jobPosition={jobPosition}
        />
      )}
      {invitationType !== 'default' && (
        <>
          <ApplicationHeader
            id={jobPosition.id}
            jobTitle={jobPosition.title}
            status={status}
            type={invitationType}
            employerName={jobPosition.employer.name}
            openDialog={openDialog}
            slug={jobPosition.slug}
          />
          <div className='bg-white'>
            <div className='-mx-6 w-auto border-b border-gray-200 sm:mx-0 lg:mx-12 lg:block' />
          </div>
        </>
      )}

      {isPendingInvite && invitationText && (
        <div className='-mx-6 bg-white px-4 pt-2 pb-2 sm:mx-0 lg:px-12'>
          <h6 className='text-default mb-6 text-lg font-medium'>
            {t('overview.offerMessage')}{' '}
            <span className='font-handwriting text-main-blue text-2xl font-normal'>{jobPosition.employer.name}</span>
          </h6>
          <InvitationText invitationText={invitationText} jobPositionId={jobPosition.id} />
          <div className='hidden border-b border-gray-200 lg:mt-8 lg:block' />
        </div>
      )}

      {/* without "-top-[1px]" sticky item doesn't work */}
      <div className='-top-px rounded-4xl bg-white'>
        {isPendingInvite && <div className='-mx-6 border-t sm:mx-0 lg:hidden' />}
        {hideWhyThisJobSection ? (
          <JobDetailTitle
            titleRef={titleRef}
            invitationType={invitationType}
            jobPositionId={jobPosition.id}
            slug={jobPosition.slug}
            status={status}
            jobPositionTitle={isAnyJobPage && isTransparentJobDetails ? (jobPosition.headline ?? jobPosition.title) : jobPosition.title}
            openDialog={openDialog}
            employerLogo={getPublicImageUrl(jobPosition.employer.logoKey)}
            employerName={jobPosition.employer.name}
            employerId={jobPosition.employer.id}
            address={perks.address}
            isInitiative={jobPosition.isInitiative}
            isCoverImageShown={isCoverImageShown}>
            <div className='min-h-16 md:min-h-10 lg:min-h-8'>
              <FeaturesBadges featuresBadges={features} />
            </div>
          </JobDetailTitle>
        ) : (
          <>
            <WhyThisJobHeader jobPosition={jobPosition} featuresBadges={features} />
            <div className='-mx-6 bg-white px-4 pb-6 sm:mx-0 sm:rounded-b-2xl lg:rounded-b-none lg:px-12'>
              <SectionViewedTracker section='why-this-job' isTest={isTestJdp} jobPositionId={jobPosition.id} isFlagReady={isFlagReady}>
                <WhyThisJobSection jobPosition={jobPosition} openDialog={openDialog} />
              </SectionViewedTracker>
            </div>
          </>
        )}
      </div>

      {/* Sticky overview on mobile - between title and summary to match staging layout */}
      <StickySection align='top' className='z-sticky sticky lg:hidden'>
        <StickyOverviewNavigation titleRef={titleRef} navigationOptions={navigationOptions} sectionVisibilityRef={sectionsOnScreenRef} />
      </StickySection>

      {/* Summary attached to title card - hidden when the Why-This-Job card is shown instead; Summary then renders in the loop below Profile */}
      {hideWhyThisJobSection && (
        <div id='summary'>
          <ScrollableSection
            anchorClassName={anchorClassName}
            onIntersectionChange={handleSectionIntersection}
            rootMargin={INTERSECTION_ROOT_MARGIN}
            sectionId={ScrollableSectionEnum.Summary}>
            <JobOverview
              anonymous={isAnonymous}
              jobPosition={jobPosition}
              perks={perks}
              status={status}
              type={invitationType}
              openDialog={openDialog}
              gallery={jobPosition.employer.gallery}
              isCoverImageShown={isCoverImageShown}
            />
          </ScrollableSection>
        </div>
      )}

      {/* Sticky overview on desktop */}
      <StickyOverviewNavigation
        className='hidden lg:block'
        titleRef={titleRef}
        navigationOptions={navigationOptions}
        sectionVisibilityRef={sectionsOnScreenRef}
      />

      {/* Data-driven section rendering: orderedSections drives both DOM order and sticky nav order per variant */}
      <div className='my-6 flex flex-col gap-6 lg:my-8 lg:gap-8 lg:px-0'>
        {orderedSections.map((sectionKey: JdpSectionKey) => {
          switch (sectionKey) {
            case ScrollableSectionEnum.Summary:
              if (hideWhyThisJobSection) return null;
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Summary}>
                  <SummaryCard
                    anonymous={isAnonymous}
                    careType={jobPosition.employer.careType}
                    formalLanguage={jobPosition.employer.formalLanguage}
                    perks={perks}
                    alwaysShowTitle
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Location:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Location}>
                  <div className='min-h-150'>
                    {jobPosition.employer.geoLocation &&
                    jobPosition.employer.patientAreas &&
                    jobPosition.employer.patientAreas.length > 0 &&
                    canHaveGeoArea(jobPosition.employer.careType) ? (
                      <MapWithZonesWrapper
                        locations={sortedLocations}
                        centralLocationId={jobPosition.employer.geoLocation.id}
                        candidateLocation={candidateLocation}
                      />
                    ) : (
                      <DirectionsMapWrapper
                        anonymous={isAnonymous}
                        candidateLocation={candidateLocation}
                        candidateTransportMode={candidate?.transportationType as TransportationType}
                        jobLocation={jobPosition.geoLocation}
                        formalLanguage={jobPosition.employer.formalLanguage}
                        geoArea={jobPosition.employer.geoLocation?.zone}
                        mapPlaceholder={Map}
                        leftDecorativeImage={BothCheckingPhoneSmiling}
                        initiallyVisible={isTest}
                      />
                    )}
                  </div>
                </ScrollableSection>
              );

            case ScrollableSectionEnum.AboutUs:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.AboutUs}>
                  <AboutUs anonymous={isAnonymous} aboutUs={jobPosition.employer?.aboutUs ?? ''} />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.ClientAssignment:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.ClientAssignment}>
                  <ClientAssignment
                    jobPositionId={jobPosition.id}
                    openDialog={openDialog}
                    status={status}
                    title={jobPosition.title}
                    employerName={jobPosition.employer.name}
                    type={invitationType}
                    typesLeasingEmployer={jobPosition.employer.typesLeasingEmployer}
                    slug={jobPosition.slug}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Differentiations:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Differentiations}>
                  <EmployerDifferentiation differentiations={jobPosition.employer.differentiations} />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Salary:
              if (!showSalary) return null;
              return (
                <ScrollableSection
                  anchorClassName={anchorClassName}
                  key={sectionKey}
                  onIntersectionChange={handleSectionIntersection}
                  ref={salaryRef}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Salary}>
                  <SalaryDetails
                    salaryHigh={jobPosition.salaryHigh}
                    formalLanguage={jobPosition.employer.formalLanguage}
                    salaryLow={jobPosition.salaryLow ? jobPosition.salaryLow : jobPosition.salaryHigh}
                    educationBonuses={jobPosition.educationPayments || []}
                    specialBonuses={jobPosition.extraPayments || []}
                    surgeIncomes={jobPosition.salaryElements || []}
                    oneYearExperience={jobPosition.basicSalary}
                    threeYearExperience={jobPosition.experienceSalary}
                    eightYearExperience={jobPosition.professionalSalary}
                    fullTimeHours={jobPosition.fullTimeHours}
                    showSalaryDetails={jobPosition.showSalaryDetails}
                    shiftDurationHours={jobPosition.shiftDurationHours}
                    additionalSalaryInformation={jobPosition.additionalSalaryInformation}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.OpeningHours:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.OpeningHours}>
                  <OpeningHours
                    openingHours={jobPosition.openingHours ?? []}
                    imageShown={!visibleSections[ScrollableSectionEnum.ShiftSystem]}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.ShiftSystem:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.ShiftSystem}>
                  <ShiftSystem
                    dayShiftType={jobPosition.shiftType}
                    careType={jobPosition.employer.careType}
                    additionalInformation={jobPosition.additionalInformation}
                    nightsPerMonth={jobPosition.nightsPerMonth}
                    weekendsPerMonth={jobPosition.weekendsPerMonth}
                    shiftCount={jobPosition.shiftCount}
                    shift1Start={jobPosition.shift1Start}
                    shift1End={jobPosition.shift1End}
                    shift2Start={jobPosition.shift2Start}
                    shift2End={jobPosition.shift2End}
                    shift3Start={jobPosition.shift3Start}
                    shift3End={jobPosition.shift3End}
                    showTimes={jobPosition.showTimes}
                    imageShown={!isTransparentPath}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Profile:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Profile}>
                  <YourProfile formalLanguage={jobPosition.employer.formalLanguage} profilePropertyList={jobPosition.candidateProfile} />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Responsibilities:
              return (
                <React.Fragment key={sectionKey}>
                  <ScrollableSection
                    anchorClassName={anchorClassName}
                    onIntersectionChange={handleSectionIntersection}
                    rootMargin={INTERSECTION_ROOT_MARGIN}
                    sectionId={ScrollableSectionEnum.Responsibilities}>
                    <Responsibilities
                      type={invitationType}
                      status={status}
                      jobPositionId={jobPosition.id}
                      title={jobPosition.title}
                      employerName={jobPosition.employer.name}
                      responsibilities={jobPosition.responsibilities}
                      openDialog={openDialog}
                      slug={jobPosition.slug}
                    />
                  </ScrollableSection>
                  {jobPosition.isInitiative && <InitiativeBanner image={initiativePersonImage} />}
                </React.Fragment>
              );

            case ScrollableSectionEnum.Benefits:
              return (
                <React.Fragment key={sectionKey}>
                  <ScrollableSection
                    anchorClassName={anchorClassName}
                    onIntersectionChange={handleSectionIntersection}
                    rootMargin={INTERSECTION_ROOT_MARGIN}
                    sectionId={ScrollableSectionEnum.Benefits}>
                    <WhatWeOffer
                      type={invitationType}
                      status={status}
                      employerName={jobPosition.employer.name}
                      jobPositionId={jobPosition.id}
                      title={jobPosition.title}
                      benefits={jobPosition.benefits}
                      openDialog={openDialog}
                      slug={jobPosition.slug}
                    />
                  </ScrollableSection>
                  {jobPosition.employer.careType !== CareType.Leasing && (
                    <DocumentationSystems anonymous={isAnonymous} documentationSystems={jobPosition.employer?.documentationSystems} />
                  )}
                  {jobPosition.howIWorkDescription && <HowIWork description={jobPosition.howIWorkDescription} anonymous={isAnonymous} />}
                </React.Fragment>
              );

            case ScrollableSectionEnum.Onboarding:
              return (
                <React.Fragment key={sectionKey}>
                  <ScrollableSection
                    anchorClassName={anchorClassName}
                    onIntersectionChange={handleSectionIntersection}
                    rootMargin={INTERSECTION_ROOT_MARGIN}
                    sectionId={ScrollableSectionEnum.Onboarding}>
                    <OnBoardingProcedure
                      onboardingText={jobPosition.onboardingProcedure}
                      image={getPublicImageUrl(jobPosition?.onboardingProcedureImageKey)}
                      anonymous={isAnonymous}
                      formalLanguage={jobPosition.employer.formalLanguage}
                    />
                  </ScrollableSection>
                  {(jobPosition.employer.bestArgumentContent || jobPosition.employer.bestArgumentQuote) && (
                    <Arguments
                      bestArgumentQuote={jobPosition.employer.bestArgumentQuote}
                      bestArgument={jobPosition.employer.bestArgumentContent}
                      author={jobPosition.employer.name}
                      anonymous={isAnonymous}
                    />
                  )}
                </React.Fragment>
              );

            case ScrollableSectionEnum.Gallery:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Gallery}>
                  <Gallery anonymous={isAnonymous} images={jobPosition.employer.gallery} />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.EmployeeTestimony:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.EmployeeTestimony}>
                  <EmployeeTestimony
                    quote={jobPosition.employer.testimonyQuote}
                    author={jobPosition.employer.testimonyAuthor}
                    jobTitle={jobPosition.employer.testimonyJobtitle}
                    employeeTestimonyPhoto={getPublicImageUrl(jobPosition.employer.testimonyImageKey)}
                    anonymous={isAnonymous}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.DailyWork:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.DailyWork}>
                  <WhatADayLooksLike
                    description={jobPosition.whatADayLooksLike}
                    image={getPublicImageUrl(jobPosition?.whatADayLooksLikeImageKey)}
                    anonymous={isAnonymous}
                  />
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Insights:
              return (
                <ScrollableSection
                  key={sectionKey}
                  anchorClassName={anchorClassName}
                  onIntersectionChange={handleSectionIntersection}
                  rootMargin={INTERSECTION_ROOT_MARGIN}
                  sectionId={ScrollableSectionEnum.Insights}>
                  <div className='min-h-75'>
                    <Insights
                      type={invitationType}
                      status={status}
                      title={jobPosition.title}
                      jobPositionId={jobPosition.id}
                      anonymous={isAnonymous}
                      embedUrl={jobPosition.employer.youtubeVideoUrl}
                      employerName={jobPosition.employer.name}
                      openDialog={openDialog}
                      slug={jobPosition.slug}
                    />
                  </div>
                </ScrollableSection>
              );

            case ScrollableSectionEnum.Career:
              return (
                !authenticated &&
                isJobDetailsPage && (
                  <ScrollableSection
                    key={sectionKey}
                    anchorClassName={anchorClassName}
                    onIntersectionChange={handleSectionIntersection}
                    rootMargin={INTERSECTION_ROOT_MARGIN}
                    sectionId={ScrollableSectionEnum.Career}>
                    <Career jobPosition={jobPosition} />
                  </ScrollableSection>
                )
              );

            case ScrollableSectionEnum.ContactPerson:
              return isTransparentPath ? (
                <ContactPersonTransparent
                  key={sectionKey}
                  contactPerson={jobPosition.employer.displayContactPerson}
                  jobPositionId={jobPosition.id}
                  jobPositionTitle={jobPosition.title}
                  employerName={jobPosition.employer.name}
                  openDialog={openDialog}
                  jobPositionSlug={jobPosition.slug}
                />
              ) : null;

            /* ── A/B test sections ── */
            case AB_TEST_SECTION_KEYS.otherJobsCarousel:
              return isTestJdp && jobs && jobs.length >= 2 ? (
                <SectionViewedTracker
                  key={sectionKey}
                  section='other-jobs-carousel'
                  isTest={isTestJdp}
                  jobPositionId={jobPosition.id}
                  isFlagReady={isFlagReady}>
                  <OtherJobsCarousel jobs={jobs as unknown as CarouselJob[]} openDialog={openDialog} />
                </SectionViewedTracker>
              ) : null;

            case AB_TEST_SECTION_KEYS.controlAnchor:
              return isFlagReady ? (
                <SectionViewedTracker
                  key={sectionKey}
                  section='control-anchor'
                  isTest={isTestJdp}
                  jobPositionId={jobPosition.id}
                  isFlagReady={isFlagReady}>
                  <></>
                </SectionViewedTracker>
              ) : null;

            default:
              return null;
          }
        })}

        {/* Non-reorderable tail sections - fixed order */}
        {jobPosition.employer.careType === CareType.Leasing && <Highlights perks={highlights} />}

        {jobPosition.employer.teamPhotoKey && (
          <OurTeam
            type={invitationType}
            formalLanguage={jobPosition.employer.formalLanguage}
            status={status}
            jobPositionId={jobPosition.id}
            title={jobPosition.title}
            image={getPublicImageUrl(jobPosition.employer.teamPhotoKey) || ''}
            anonymous={isAnonymous}
            employerName={jobPosition.employer.name}
            openDialog={openDialog}
            slug={jobPosition.slug}
          />
        )}

        <RecommendationComponent formalLanguage={jobPosition.employer.formalLanguage} />
        {(relatedPositionsLoading || !isEmpty(relatedPositions)) && (
          <OtherOpenPositions
            className='mt-8'
            openDialog={openDialog}
            anonymous={isAnonymous}
            total={relatedPositions.length}
            loading={relatedPositionsLoading}
            jobs={relatedPositions}
            titleKey='jobDetails:otherPositions.title'
          />
        )}

        {!isPraxia && isJobDetailsPage && (
          <NavigationCluster
            alternativeJobTitlesByPriority={alternativeJobTitlesByPriority}
            city={jobPosition.geoLocation?.city}
            nearbyCities={nearbyCities}
          />
        )}

        {isTransparentJobDetails && (
          <>
            <MagazinSliderSection jobPositionJobTitles={jobPosition.jobTitles} />
            <JobPositionFaqSection jobPosition={jobPosition} />
          </>
        )}
      </div>

      {offersDialogueShown && <OffersDialogueModal />}
      {suggestionModalAllowed && <WrappedSuggestionsModal enableOnExit={false} trigger={SuggestionsTrigger.IgnoreInvite} />}

      {showPausedModal && (
        <Modal className='pb-6' size={relatedPositions.length === 0 ? 'default' : 'large'}>
          <JobPausedWithSuggestions jobsAvailable={relatedPositions.length !== 0} />
          <OtherOpenPositions
            openDialog={openDialog}
            anonymous={isAnonymous}
            total={relatedPositions.length}
            loading={relatedPositionsLoading}
            jobs={relatedPositions}
            titleKey='jobDetails:otherPositions.title'
            useSlimCard
            onSlimCardLinkClick={() => setShowPausedModal(false)}
          />
          {relatedPositions.length > 0 && (
            <div className='mt-4 flex justify-center'>
              <Link
                className={getButtonClassName('primary')}
                href={{
                  pathname: [
                    JOBS,
                    jobPosition.jobTitles?.[0]?.title &&
                      kebabCase(
                        t(`jobTitles:${camelCase(jobPosition.jobTitles[0].title)}`, {
                          defaultValue: jobPosition.jobTitles[0].title,
                        }),
                      ),
                    jobPosition.geoLocation.city && kebabCase(jobPosition.geoLocation.city),
                  ]
                    .filter(Boolean)
                    .join('/'),
                }}>
                {t('seeAllJobs')}
              </Link>
            </div>
          )}
        </Modal>
      )}
    </ContainerSection>
  );

  return (
    <div className='bg-background-secondary relative h-full pt-0 sm:pt-2'>
      <div className='mx-auto mt-0 overflow-hidden sm:mt-6 xl:container 2xl:max-w-7xl'>{Details}</div>
      {['accepted', 'pending'].includes(status) && (
        <StickyBottomSection
          className='py-4 lg:hidden'
          invitationType={invitationType}
          jobPosition={jobPosition}
          openDialog={openDialog}
          status={status}
          applyLabel={t('whyThisJob.stickyCta')}
        />
      )}
    </div>
  );
};

const JobDetail: React.FC<JobDetailProps> = props => {
  const { isJobDetailsPage } = useRouterHelper();
  const { isPraxia } = useFlavorFromRoute();

  const {
    jobPosition: {
      id: jobPositionId,
      title,
      employer: { id: employerId, status: employerStatus },
      geoLocation,
      jobTitles,
      headline,
      status,
      isInitiative,
    },
  } = props;

  const titleSources = [title, headline].filter(Boolean);
  const relatedJobsFilters = {
    employerId,
    excludedJobPositionIds: [jobPositionId],
    location: geoLocation?.location as Point,
    jobTitleIds: jobTitles?.map(jt => jt.id),
    jobPositionTitleQueries: Array.from(new Set(titleSources)) as string[],
  };
  const isJobPositionPaused = status === JobPositionStatus.Paused || employerStatus === EmployerStatus.LocationPaused;

  return (
    <JobDetailsProvider
      employerId={employerId}
      jobPositionId={jobPositionId}
      positionRelatedJobsFilters={relatedJobsFilters}
      isJobPositionPaused={isJobPositionPaused}
      isInitiative={isInitiative}>
      {!isPraxia && isJobDetailsPage && (
        <div className='bg-trustpilot-banner flex h-13 items-center justify-center'>
          <div className='w-full'>
            <TrustpilotWidget type={TrustpilotWidgetType.Horizontal} theme='dark' />
          </div>
        </div>
      )}
      <JobDetailBase {...props} />
    </JobDetailsProvider>
  );
};

export default JobDetail;
